import { SortId, SortOrder, SortValue, SortValueDeserialized } from './DataView.types';

/**
 * Receives a SortValue (ex: `'-product_name'`) and returns an object with a `id` and an `order`.
 */
export function deserializeSortValue(sort: SortValue): SortValueDeserialized {
  const order = sort[0] === '-' ? 'desc' : 'asc';
  const id = order === 'asc' ? sort : sort.slice(1);

  return { id, order };
}

/**
 * Receives an object with a `id` and an `order` and returns a SortValue (ex: `'-product_name'`).
 */
export function serializeSortValue({ id, order }: SortValueDeserialized): SortValue {
  return `${order === 'desc' ? '-' : ''}${id}`;
}

interface GetNextSortArgs {
  newId: SortId | undefined;
  newOrder?: SortOrder;
  oldId?: SortId;
  oldOrder?: SortOrder;
}

/**
 * Given new and old sorting data, it returns the next sorting value using the following sorting cycle:
 *
 *     "unsorted --> ascending --> descending --> unsorted"
 */
export function getNextSort({ newId, newOrder, oldId, oldOrder }: GetNextSortArgs): SortValue | undefined {
  if (!newId) {
    return undefined;
  }

  if (newId !== oldId) {
    // if sorting by a new field, sort ascending
    return serializeSortValue({ id: newId, order: newOrder || 'asc' });
  }

  /**
   * `newOrder` and `oldOrder` can each have 3 different values: undefined, 'asc', or 'desc'.
   * Therefore, there are 9 possible combinations (3 times 3). See below:
   */

  // #region newOrder: undefined
  if (!newOrder && !oldOrder) {
    return newId; // ascending
  }

  if (!newOrder && oldOrder === 'desc') {
    return undefined; // unsorted
  }

  if (!newOrder && oldOrder === 'asc') {
    return `-${newId}`; // descending
  }
  // #endregion

  // #region newOrder: asc
  if (newOrder === 'asc' && !oldOrder) {
    return newId;
  }

  if (newOrder === 'asc' && oldOrder === 'asc') {
    return undefined;
  }

  if (newOrder === 'asc' && oldOrder === 'desc') {
    return newId;
  }
  // #endregion

  // #region newOrder: desc
  if (newOrder === 'desc' && !oldOrder) {
    return `-${newId}`;
  }

  if (newOrder === 'desc' && oldOrder === 'desc') {
    return undefined;
  }

  if (newOrder === 'desc' && oldOrder === 'asc') {
    return `-${newId}`;
  }
  // #endregion
}
